import { useAuth } from '@auth/useAuth'
import { useEffect, useRef } from 'react'
import { identifyLogRocket, isLogRocketEnabled, resetLogRocketSession } from './logRocket'
import { useCurrentMember } from './useCurrentMember'
import { useCurrentTenant } from './useCurrentTenant'

/**
 * Non-rendering component that pushes the currently signed-in user/tenant over
 * to LogRocket so session replays and errors are attributed to the right person
 * and account. The underlying `identifyLogRocket` helper is a no-op when the
 * integration is disabled, so this component is safe to mount in all
 * environments.
 *
 * On logout it starts a fresh LogRocket session (`resetLogRocketSession`). The
 * SPA does not reload between logout and a subsequent login on the same page
 * load, so without this reset the next user's activity would be recorded into
 * the previous user's replay under the prior identity.
 *
 * Must be mounted inside `<AuthProvider>` and `<QueryClientProvider>` so the
 * underlying hooks have access to the auth context and the React Query cache.
 *
 * SYN-7849: POC — LogRocket session replay & debugging evaluation.
 */
const LogRocketIdentifier: React.FC = () => {
  const { isAuthenticated, isTenantScope } = useAuth()
  const [tenant] = useCurrentTenant()
  const { member } = useCurrentMember()

  const tenantMemberId = member?.id
  const tenantMemberCreatedAt = member?.createdAt
  const memberRole = member?.role
  const tenantAccountNumber = tenant?.accountNumber
  const tenantName = tenant?.name
  const tenantLicenseType = tenant?.licenseType

  // Tracks whether we have identified a user into the current LogRocket session
  // so we only reset it once, on the transition into a logged-out state.
  const hasIdentifiedRef = useRef(false)

  useEffect(() => {
    if (!isLogRocketEnabled()) return

    // Only a genuine end of the tenant auth session should reset the replay. If
    // we previously identified someone, start a fresh session so the next login
    // is not blended into the prior user's replay.
    if (!isAuthenticated || !isTenantScope) {
      if (hasIdentifiedRef.current) {
        resetLogRocketSession()
        hasIdentifiedRef.current = false
      }
      return
    }

    // Still an authenticated tenant session, but the member/tenant data is not
    // available yet (initial load, refetch, or a transient `useCurrentMember` /
    // `useCurrentTenant` error). Wait for it rather than treating it as a
    // logout: resetting here would fragment the active user's replay into a new
    // session on every transient error.
    if (!tenantMemberId || !tenantAccountNumber) return

    identifyLogRocket(
      {
        id: tenantMemberId,
        role: memberRole,
        createdAt: tenantMemberCreatedAt,
      },
      {
        id: tenantAccountNumber,
        name: tenantName,
        licenseType: tenantLicenseType,
      },
    )
    hasIdentifiedRef.current = true
  }, [
    isAuthenticated,
    isTenantScope,
    tenantMemberId,
    tenantMemberCreatedAt,
    memberRole,
    tenantAccountNumber,
    tenantName,
    tenantLicenseType,
  ])

  return null
}

export default LogRocketIdentifier
